import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaShoppingCart, FaCheck, FaUndo, FaShareAlt } from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { shareContent, buildCaption } from '../utils/share';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { isWebProduct } from '../utils/productVisibility';
import './StyleLookModal.css';

const fmt = (n) => `KSh ${Number(n || 0).toLocaleString()}`;

// Editorial "lookbook" presentation of a styled outfit: a full lifestyle photo
// on the left, and a cream panel on the right listing each piece (captioned),
// a colour palette, and "why this look" notes. It stays shoppable — the
// customer can drop pieces, sees the combo discount, and adds the rest in one tap.
const StyleLookModal = ({ look, onClose }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [removed, setRemoved] = useState(new Set());
  const [added, setAdded] = useState(false);
  const [shareMsg, setShareMsg] = useState('');
  // Pieces that have since been pulled from the website. A look stores a
  // denormalised snapshot of its products, so it can outlive their publishing
  // status — re-check on open rather than offering a bundle we can't sell.
  const [unavailable, setUnavailable] = useState(() => new Set());

  // Lock background scroll while open; always restore on unmount so a leftover
  // lock can never strand the page.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const allProducts = look.products || [];

  useEffect(() => {
    let alive = true;
    const ids = allProducts.map(p => p.productId).filter(Boolean);
    if (ids.length === 0) return undefined;

    (async () => {
      // Start pessimistic, then clear every id that comes back published.
      const gone = new Set(ids);
      try {
        for (let i = 0; i < ids.length; i += 10) {
          const snap = await getDocs(query(
            collection(db, 'products'),
            where(documentId(), 'in', ids.slice(i, i + 10))
          ));
          snap.forEach(d => { if (isWebProduct(d.data())) gone.delete(d.id); });
        }
      } catch (e) {
        // Fail open — a transient Firestore error shouldn't gut the look.
        console.error('Look availability check failed:', e);
        gone.clear();
      }
      if (alive) setUnavailable(gone);
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [look.id]);

  const remaining = allProducts.filter(
    p => !removed.has(p.productId) && !unavailable.has(p.productId)
  );
  const discount = Math.min(90, Math.max(0, Number(look.discountPercent) || 0));

  // Optional editorial fields (older looks won't have them — fall back gracefully).
  const subtitle = look.subtitle || '';
  const description = look.description || '';
  const palette = Array.isArray(look.palette) ? look.palette.filter(Boolean) : [];
  const why = Array.isArray(look.why) ? look.why.filter(Boolean) : [];

  const handleShare = async () => {
    const url = `${window.location.origin}/#picked-for-you`;
    const caption = buildCaption({
      title: look.title || 'Shop this look',
      description: description || subtitle,
      url,
    });
    const result = await shareContent({
      title: look.title || 'Shop this look',
      text: caption,
      url,
      imageUrl: look.imageUrl,
    });
    if (result === 'copied') {
      setShareMsg('Caption copied — paste it into Instagram');
      setTimeout(() => setShareMsg(''), 3000);
    }
  };

  const basePrice = (p) => p.discountPrice || p.price || 0;
  const comboPrice = (p) => Math.round(basePrice(p) * (1 - discount / 100));

  const subtotal = remaining.reduce((s, p) => s + basePrice(p), 0);
  const total = remaining.reduce((s, p) => s + comboPrice(p), 0);
  const saved = subtotal - total;

  const toggleRemove = (id) =>
    setRemoved(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const addBundle = () => {
    remaining.forEach(p => {
      const size = (p.sizes && p.sizes[0]) || '';
      const color = (p.colors && p.colors[0]) || '';
      const cp = comboPrice(p);
      addToCart(p.productId, size, color, 1, {
        name: p.name,
        price: p.price || 0,
        // Only mark a discount when the combo price is genuinely lower, so
        // non-discounted bundles don't render a "Save KSh 0" line.
        discountPrice: cp < (p.price || 0) ? cp : null,
        images: [p.image],
        imageUrl: p.image,
        sizes: p.sizes || [],
        colors: p.colors || [],
        category: p.category || '',
        comboId: look.id,
        comboTitle: look.title,
      });
    });
    setAdded(true);
  };

  return createPortal((
    <div className="slm-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="slm-sheet" role="dialog" aria-label={look.title || 'Styled look'}>
        <button className="slm-close" onClick={onClose} aria-label="Close"><FaTimes /></button>
        <button className="slm-share-btn" onClick={handleShare} aria-label="Share this look" title="Share this look">
          <FaShareAlt />
        </button>

        {/* Left — full lifestyle photo */}
        <div className="slm-photo">
          <img src={look.imageUrl} alt={look.title || 'Styled look'} />
          {discount > 0 && <span className="slm-disc-badge">−{discount}% bundle</span>}
        </div>

        {/* Right — cream lookbook panel */}
        <div className="slm-panel">
          <div className="slm-panel-scroll">
            <header className="slm-lb-head">
              <h2 className="slm-lb-title">{look.title || 'Shop the Look'}</h2>
              {subtitle && <p className="slm-lb-sub">{subtitle}</p>}
            </header>

            {description && <p className="slm-lb-desc">{description}</p>}
            {shareMsg && <p className="slm-share-msg"><FaCheck /> {shareMsg}</p>}

            {allProducts.length === 0 ? (
              <p className="slm-empty-products">This look isn’t linked to products yet.</p>
            ) : (
              <div className="slm-pieces">
                {allProducts.map(p => {
                  const offWeb = unavailable.has(p.productId);
                  const isOut = offWeb || removed.has(p.productId);
                  return (
                    <div key={p.productId} className={`slm-piece${isOut ? ' out' : ''}`}>
                      <div className="slm-piece-imgwrap">
                        <img src={p.image} alt={p.caption || p.name} className="slm-piece-img" />
                        {offWeb ? (
                          <span className="slm-piece-offweb">In store only</span>
                        ) : (
                          <button
                            className="slm-piece-toggle"
                            onClick={() => toggleRemove(p.productId)}
                            title={isOut ? 'Add back to bundle' : 'Remove from bundle'}
                            aria-label={isOut ? 'Add back' : 'Remove'}
                          >
                            {isOut ? <FaUndo /> : <FaTimes />}
                          </button>
                        )}
                      </div>
                      <span className="slm-piece-cap">{p.caption || p.name}</span>
                      <span className="slm-piece-price">
                        {discount > 0 && !isOut ? (
                          <>
                            <span className="slm-now">{fmt(comboPrice(p))}</span>
                            <span className="slm-was">{fmt(basePrice(p))}</span>
                          </>
                        ) : (
                          <span className="slm-now">{fmt(basePrice(p))}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {palette.length > 0 && (
              <div className="slm-block">
                <span className="slm-lb-label">Colour Palette</span>
                <div className="slm-swatches">
                  {palette.map((c, i) => (
                    <span
                      key={i}
                      className="slm-swatch"
                      style={{ background: c }}
                      title={c}
                    />
                  ))}
                </div>
              </div>
            )}

            {why.length > 0 && (
              <div className="slm-block">
                <span className="slm-lb-label">Why this look?</span>
                <ul className="slm-why">
                  {why.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
          </div>

          {/* Sticky footer — totals + CTA */}
          {allProducts.length > 0 && (
            <div className="slm-foot">
              {remaining.length > 0 && (
                <div className="slm-totals">
                  {discount > 0 && (
                    <div className="slm-total-row slm-saved">
                      <span>Bundle saving</span>
                      <span>− {fmt(saved)}</span>
                    </div>
                  )}
                  <div className="slm-total-row slm-grand">
                    <span>Total · {remaining.length} of {allProducts.length} pieces</span>
                    <span>{fmt(total)}</span>
                  </div>
                </div>
              )}

              {added ? (
                <div className="slm-actions">
                  <div className="slm-added"><FaCheck /> Added to cart</div>
                  <button className="slm-cta" onClick={() => { onClose(); navigate('/cart'); }}>
                    Go to Cart
                  </button>
                </div>
              ) : (
                <button className="slm-cta" onClick={addBundle} disabled={remaining.length === 0}>
                  <FaShoppingCart /> Add bundle · {fmt(total)}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  ), document.body);
};

export default StyleLookModal;
